import {
  useEffect,
  useRef,
  forwardRef,
  useMemo,
  useImperativeHandle,
  MouseEvent,
} from 'react'
import Box from '@mui/material/Box'
import ConstructionPlanner from '../../visualization/main'
import { useDispatch, useSelector } from 'react-redux'
import store, { RootState } from '../../store'
import { ConstructionPage, ConstructionTab } from '../../reducers/construction'
import {
  BuildingElementStatus,
  BuildingElementType,
  BuildingObjectTool,
  BuildingEditingTool,
  BuildingStructuralObjectTool,
  BuildingDocumentType,
  HideShowOption,
} from '../../models/Construction'
import OptionsModal from '../Validation/OptionsModal'
import { EventType, StoreyElementsVisibility } from '../../utils/EventEmitter'
import {
  setIsAddCommentMode,
  updateAddCommentInfo,
} from '../../actions/comments'
import { commentsCursor } from '../../models/Comment'
import { StructuralAnimationStatus } from '../../reducers/structural'
import {
  endStructuralAnimation,
  playStructuralAnimation,
} from '../../actions/structural'
import { fetchPermitFileAsync } from '../../services/filesService'
import { CodeValidationRuleStatus } from '../../models/Validation'
import {
  resetBuildingElementsStatus,
  resetLastUpdatedElementId,
  setSelectedElementsIds,
  updateStatusMessage,
} from '../../actions/construction'
import { AbstractMesh } from '@babylonjs/core'
import { redo, undo } from '../../actions/history'
import { createSelector } from '@reduxjs/toolkit'
import { filterBuildingByIds } from '../../utils/ConstructionUtil'
import {
  fetchBuildingDocument,
  fetchBuildingInfo,
  updateBuildingInfo,
} from '../../services/buildingService'
import { SelectionManager } from '../../visualization/selection/selectionManager'
import {
  CreateBuildingPayload,
  SnapWallElementsPayload,
} from '../../visualization/constructionBuilder'
import { usePrevious } from '../../hooks/usePrevious'
import { isSelectionIgnored } from '../../visualization/utils'
import { BimObject } from '../../models/Bim'
import { collectBimObjectRefIds } from '../../utils/BimUtil'
import { getBimObjectsAsync } from '../../services/bimService'

const addCursor =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACEAAAAhCAYAAABX5MJvAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAANSSURBVHgBxVZrbtpAEJ71AyGlUl0pwfSlOicoPUHcG3ADyAniG4SeAHICuAHNCfANTE5gWl6pKsXuvyTAbmeITR2DDQ5O+kkre9f27jevbwxCiLp4QJfu4X8AD7YcxxH1el30ej0i4+Fo4zDhpYCHaTc3N56maYKmhmFECbkBoQo8N/CQnmVZSxLRQYRarZZwXTckZOEw4DlArifL4ySiwzRN0el0QkK9IJcMyAsUEsqFMCTbRrVaXRLKPaFxo1aj0diJRDiIdCx/LNgHu4QkiQh5hiqMkhj2BYWEYr/L4fRes9kUnueFOVKFPIAbNdJCQlZTFUX0pEH5BHmC9IAsgwTLI1ab8JygQzaFpNulInghWacMJ4GCmGhR9qd9V4apUSpMz3V10ispY09XxyIYTkkdtw+VoZn0rbRhrVOr1QDjv1pAz9DF3rSBBq6GBze5wh2JzxkTi2/3r+6Pf83es7uD+zccxCkDcSUzqa0XJt0yuAbsAgoJlR0EnghUsrLJerTU1dVhS9PcrQmqqyML3/dK8s/tlUSxDzWD8gPhJBEoq6M6ZMCROqpgeLxySnhCEisZb7fbGxNySaAwbMATQATIIxRK2EKkQ2IUwIg+Q2vq6FoH9oCuDFulbUaQFgS6sCbFSy9scSdWiPO2OPmU9HyZ0IWxS1cl6SXGmI0EvuJtP7pOMaXr9fyjDSlgDFJd7cOxr/PJQJXVqpK+EbPX1jg3QJavIA8wfikxUVGyf8gqgvN+fJmqZSEvzHCOpaXxBVSP5NEfmstMDOLe4xx8Jkmvs5NIgjI3GJNOHq1hXjGJ+SEpiAmeQGKolrXMJIRgA9z4JL4eWGmHc0xeU1KYNb199yNpLxm9ijteSZAVEvQZQC5/3wLYZwasn5nE79mHPrrWCKtkH6AxJsgzO7snCIJfoAVnaa9g8/qSFgpdvT5DY+zr2+MBPAUPnXO7YCVh2fax3ZeLO3bUJFA4SP+zhmXV+J7Yd9Y3pB6C0nu0Yyc9fGhcawQY7IlycWqIBe/hrY8/MBez2ew7SXL4fNkbFKUiM/mcftKY4Kdx0dqbxIoMekMIqKGimjgd4NYDOhTvDRQXG0+6vDuYdXz/H8HcSTwiRAk3VwxU0cFtEfxNB0fxFzBxghFGvwceAAAAAElFTkSuQmCC'

const historySelector = createSelector(
  [(state: RootState) => state.history],
  (history) => history,
)
const selectSelectedElementsIds = createSelector(
  [
    (state: RootState) => state.construction.selectedTool,
    (state: RootState) => state.construction.selectedElementsIds,
  ],
  (selectedTool, selectedElementsIds) => {
    if (isSelectionIgnored(selectedTool)) {
      return []
    } else {
      return selectedElementsIds
    }
  },
)

const selectedBimObjectIdSelector = createSelector(
  [(state: RootState) => state.bim],
  (bim) => bim.selectedBimObjectId,
)
const buildingBimObjectsSelector = createSelector(
  [(state: RootState) => state.construction],
  (construction) => construction.bimObjects,
)

export interface ViewerRef {
  get3DViewImage: () => Promise<string>
}

const Viewer = forwardRef<ViewerRef, {}>((_, ref) => {
  const [viewImageTakenPromise, resolvePromise, rejectPromise] = useMemo(() => {
    let resolvePromise: (value: void | PromiseLike<void>) => void = () => {}
    let rejectPromise: (reason?: any) => void = () => {}

    const viewImageTakenPromise = new Promise<void>((resolve, reject) => {
      resolvePromise = resolve
      rejectPromise = reject
    })

    return [viewImageTakenPromise, resolvePromise, rejectPromise]
  }, [])

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const planner = useRef<ConstructionPlanner>()
  const dispatch = useDispatch<any>()
  const fileName = useSelector(
    (state: RootState) => state.construction.fileName,
  )
  const contextId = useSelector(
    (state: RootState) => state.construction.convertData.context_id,
  )
  const showAnimation = useSelector(
    (state: RootState) => state.construction.showSketchAnimation,
  )
  const building = useSelector(
    (state: RootState) => state.construction.convertData.building,
  )
  const buildingHasElements = useSelector(
    (state: RootState) => state.construction.convertData.building.has_elements,
  )
  const buildingHasStructuralElements = useSelector(
    (state: RootState) =>
      state.construction.convertData.building.has_structural,
  )
  const buildingElementsStatus = useSelector(
    (state: RootState) => state.construction.buildingElementStatus,
  )
  const buildingComplete = buildingElementsStatus
    .filter((item) => item.elementType !== BuildingElementType.Structural)
    .every((element) => element.status === BuildingElementStatus.Complete)
  const structuralElementsComplete =
    buildingElementsStatus.find(
      (item) => item.elementType === BuildingElementType.Structural,
    )?.status === BuildingElementStatus.Complete
  const selectedStoreyId = useSelector(
    (state: RootState) => state.construction.selectedStoreyId,
  )
  const selectedBuildingMode = useSelector(
    (state: RootState) => state.construction.selectedBuildingMode,
  )
  const selectedConstructionTab = useSelector(
    (state: RootState) => state.construction.selectedConstructionTab,
  )
  const selectedTool = useSelector(
    (state: RootState) => state.construction.selectedTool,
  )
  const gridSettings = useSelector(
    (state: RootState) => state.construction.gridSettings,
  )
  const hideShowOptions = useSelector(
    (state: RootState) => state.construction.hideShowOptions,
  )
  const selectedConstructionPage = useSelector(
    (state: RootState) => state.construction.selectedConstructionPage,
  )
  const selectedStructuralTypes = useSelector(
    (state: RootState) => state.structural.selectedStructuralTypes,
  )
  const structuralAnimationStatus = useSelector(
    (state: RootState) => state.structural.structuralAnimationStatus,
  )
  const lastUpdatedElementId = useSelector(
    (state: RootState) => state.construction.lastUpdatedElementId,
  )
  const selectedElementsIds = useSelector(selectSelectedElementsIds)

  const isCommentsMode = useSelector(
    (state: RootState) => state.comments.isCommentsMode,
  )
  const isAddCommentMode = useSelector(
    (state: RootState) => state.comments.isAddCommentMode,
  )
  const buildingBimObjects = useSelector(buildingBimObjectsSelector)

  const permitFile = useSelector((state: RootState) => state.permit.file)

  const is3DViewImageRequestedRef = useRef(false)
  const isBuildingCreated = useRef(false)

  const handleAddCommentClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!isCommentsMode) {
      return
    }

    dispatch(setIsAddCommentMode(!isAddCommentMode))
    dispatch(
      updateAddCommentInfo({
        position: { x: event.clientX, y: event.clientY },
      }),
    )
  }

  useImperativeHandle(
    ref,
    () => ({
      get3DViewImage: async () => {
        is3DViewImageRequestedRef.current = true
        let constructionImage = ''
        try {
          constructionImage = await planner.current!.getConstructionImage()
          resolvePromise()
        } catch {
          rejectPromise()
        }

        return constructionImage
      },
    }),
    [],
  )

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      // Prevent default zoom behavior on the entire page
      if (event.ctrlKey) {
        event.preventDefault()
      }
    }

    const handleKeyDown = async (event: KeyboardEvent) => {
      if (!event.ctrlKey || (event.key !== 'z' && event.key !== 'x')) {
        return
      }

      event.preventDefault()

      // Get current history state
      let historyUpdated = historySelector(store.getState())

      // Determine if we should undo or redo based on the key and if it's possible
      const isUndo = event.key === 'z'
      const canPerformAction = isUndo
        ? historyUpdated.canUndo
        : historyUpdated.canRedo

      if (!canPerformAction) {
        return
      }

      // Perform the action
      dispatch(isUndo ? undo() : redo())

      // Get updated history after the action
      historyUpdated = historySelector(store.getState())
      const stack = isUndo ? historyUpdated.redoStack : historyUpdated.undoStack
      const items = stack[stack.length - 1].items
      const itemIds = items?.map((item) => item.itemId)

      // Process building
      const building = filterBuildingByIds(
        historyUpdated.state.convertData.building,
        itemIds,
      )

      // Start create building only for Architecture page
      const statusSuccess =
        selectedConstructionPage === ConstructionPage.Architecture
          ? await planner.current?.createBuilding(building, {
              itemIds,
            })
          : true
      if (statusSuccess) {
        planner.current?.buildingCreated(building)
      }
    }

    window.addEventListener('wheel', handleWheel, { passive: false })
    window.addEventListener('keydown', handleKeyDown)

    const canvas = canvasRef.current
    if (canvas) {
      canvas.width = canvas.parentElement!.clientWidth
      canvas.height = canvas.parentElement!.clientHeight

      planner.current = new ConstructionPlanner(canvas)

      planner.current.eventEmitter.on(
        EventType.StoreyElementVisibilityChanged,
        (hiddenElements: StoreyElementsVisibility) => {
          planner.current?.changeStoreyElementVisibility(hiddenElements)
        },
      )

      planner.current.eventEmitter.on(
        EventType.CodeValidationNewDataFetch,
        async (data: CodeValidationRuleStatus[]) => {
          await planner.current?.playCodeValidationAnimation(data)
        },
      )

      planner.current.eventEmitter.on(
        EventType.BuildingRemoveElement,
        (element: AbstractMesh) => {
          planner.current?.removeBuildingElement(element)
        },
      )

      planner.current.eventEmitter.on(
        EventType.BuildingSnapWallElements,
        (data: SnapWallElementsPayload) => {
          planner.current?.snapTwoWallsElements(data)
        },
      )

      planner.current.eventEmitter.on(
        EventType.BuildingCreateBuilding,
        async (data: CreateBuildingPayload) => {
          const { building, removedWallIds, addedWallIds } = data
          await planner.current?.createBuilding(building, {
            itemIds: removedWallIds,
          })

          if (addedWallIds) {
            planner.current?.addPickTriggers(addedWallIds)
          }
        },
      )

      planner.current.eventEmitter.on(
        EventType.StructuralAutomationFinished,
        async (data: CreateBuildingPayload) => {
          const { building } = data
          await planner.current?.clearStructuralElements()
          await planner.current?.createStructuralElements(building)
        },
      )

      planner.current.eventEmitter.on(
        EventType.ReplaceBuildingElementOnBimObject,
        async (payload: {
          elementsIdsToReplace: string[]
          bimObject: BimObject
        }) => {
          await planner.current?.replaceBuildingElements(
            payload.elementsIdsToReplace,
            payload.bimObject,
            payload.bimObject.isNonEditable,
          )
        },
      )

      const timeInterval = 10000 // 10 seconds
      const intervalId = setInterval(() => {
        const current = new Date()
        const historyUpdated = historySelector(store.getState())
        if (
          historyUpdated.updatedAt &&
          current.getTime() - new Date(historyUpdated.updatedAt).getTime() >
            timeInterval
        ) {
          dispatch(
            updateBuildingInfo({
              fileKey: historyUpdated.state.convertData.json_s3_key,
              building_json: historyUpdated.state.convertData,
              elementId: null,
              silent: true,
            }),
          )
          // Update 2D floorplan after 15 seconds
          setTimeout(
            () =>
              dispatch(
                fetchBuildingDocument({
                  filePath: historyUpdated.state.convertData.json_s3_key,
                  doc_type: BuildingDocumentType.Floorplans2D,
                }),
              ),
            15000,
          )
        }
      }, timeInterval)

      return () => {
        window.removeEventListener('wheel', handleWheel)
        window.removeEventListener('keydown', handleKeyDown)
        clearInterval(intervalId)
        if (!is3DViewImageRequestedRef.current) {
          planner.current?.dispose()
          planner.current = undefined
        } else {
          viewImageTakenPromise
            .then(() => {
              planner.current?.dispose()
              planner.current = undefined
            })
            .catch()
        }
      }
    }
  }, [is3DViewImageRequestedRef, viewImageTakenPromise])

  useEffect(() => {
    const abortController = new AbortController()
    const checkAndCreateBuilding = async () => {
      try {
        if (!isBuildingCreated.current) {
          const buildingHasAllElements = Object.values(
            buildingHasElements,
          ).every((value) => !!value)

          dispatch(resetBuildingElementsStatus())
          const statusSuccess = await planner.current?.createBuilding(
            building,
            { showAnimation },
          )
          if (buildingHasAllElements) {
            if (statusSuccess) {
              planner.current?.buildingCreated(building)
            }

            isBuildingCreated.current = true
            if (!!!permitFile) {
              dispatch(fetchPermitFileAsync())
            }

            const bimobjectRefs = collectBimObjectRefIds(building)
            if (bimobjectRefs.size) {
              dispatch(
                getBimObjectsAsync({
                  signal: abortController.signal,
                  bimObjectsIds: Array.from(bimobjectRefs),
                }),
              )
            }
          }
        } else if (building.has_structural && !structuralElementsComplete) {
          planner.current?.createStructuralElements(building)
        } else {
          if (lastUpdatedElementId) {
            const filteredBuilding = filterBuildingByIds(building, [
              lastUpdatedElementId,
            ])

            await planner.current?.createBuilding(filteredBuilding, {
              itemIds: [lastUpdatedElementId],
            })
            planner.current?.buildingCreated(building)

            const selectionManager = SelectionManager.getInstance()
            selectionManager.select({
              elementsIds: [lastUpdatedElementId],
              silent: true,
            })
            dispatch(setSelectedElementsIds([lastUpdatedElementId]))
            dispatch(resetLastUpdatedElementId())
          } else {
            await planner.current?.createBuilding(building)
            planner.current?.buildingCreated(building)
            const selectionManager = SelectionManager.getInstance()
            selectionManager.select({
              elementsIds: selectedElementsIds,
              silent: true,
            })
            dispatch(updateStatusMessage('Done'))
          }
        }
      } catch (e) {
        console.error(e)
      }
    }

    checkAndCreateBuilding()

    return () => {
      abortController.abort()
    }
  }, [contextId])

  useEffect(() => {
    if (planner.current && selectedStoreyId) {
      planner.current.activateBuildingItem(selectedStoreyId)
    }
  }, [selectedStoreyId])

  useEffect(() => {
    if (planner.current && buildingComplete) {
      planner.current.changeBuildingMode(selectedBuildingMode, selectedStoreyId)
    }
  }, [selectedBuildingMode, selectedStoreyId, buildingComplete])

  const prevHideShowOptions = usePrevious(hideShowOptions)
  useEffect(() => {
    if (!prevHideShowOptions) {
      return
    }

    for (const option of Object.values(HideShowOption)) {
      const prevOption = prevHideShowOptions[option]
      const currentOption = hideShowOptions[option]
      if (prevOption === currentOption) {
        continue
      }

      switch (option) {
        case HideShowOption.RoofsVisibility:
          planner.current?.toggleRoofVisibility(currentOption)
          break
        case HideShowOption.WallsTransparency:
          planner.current?.toggleWallTransparency(currentOption)
          break
        case HideShowOption.GridVisibility:
          planner.current?.toggleGridVisibility(currentOption)
          break
      }
    }
  }, [prevHideShowOptions, hideShowOptions])

  useEffect(() => {
    if (
      !planner.current ||
      selectedConstructionPage !== ConstructionPage.Engineering ||
      structuralAnimationStatus !== StructuralAnimationStatus.Pending
    ) {
      return
    }

    dispatch(playStructuralAnimation())

    planner.current.playStructuralFadeAnimation().then(() => {
      dispatch(endStructuralAnimation())
      planner.current?.toggleStructuralElementsPicking(true)
    })
  }, [dispatch, selectedConstructionPage, structuralAnimationStatus])

  useEffect(() => {
    if (!planner.current) {
      return
    }

    switch (selectedConstructionPage) {
      case ConstructionPage.Architecture:
        planner.current.toggleStructuralElements([])
        break
      case ConstructionPage.Engineering:
        if (
          selectedConstructionTab === ConstructionTab.Editor &&
          isBuildingCreated.current
        )
          planner.current.toggleStructuralElements(selectedStructuralTypes)
        else planner.current.toggleStructuralElements([])
        break
    }

    /*planner.current.updateCameraTargetScreenOffset(
      selectedConstructionTab === ConstructionTab.Validation ? 425 : 0,
    )*/
  }, [
    selectedConstructionPage,
    selectedStructuralTypes,
    selectedConstructionTab,
    isBuildingCreated,
  ])

  useEffect(() => {
    // TODO: Make condition for the case when element will not be presented at all
    const interval = setInterval(() => {
      const buildingHasAllElements = Object.values(buildingHasElements).every(
        (value) => !!value,
      )
      if (!buildingHasAllElements || !buildingHasStructuralElements) {
        dispatch(fetchBuildingInfo({ fileKey: fileName }))
      } else {
        clearInterval(interval)
        // Update building documents after 15 seconds
        setTimeout(() => {
          dispatch(
            fetchBuildingDocument({
              filePath: fileName,
              doc_type: BuildingDocumentType.Report,
            }),
          )
          dispatch(
            fetchBuildingDocument({
              filePath: fileName,
              doc_type: BuildingDocumentType.Structural,
            }),
          )
          dispatch(
            fetchBuildingDocument({
              filePath: fileName,
              doc_type: BuildingDocumentType.Floorplans2D,
            }),
          )
        }, 15000)
      }
    }, 10000)

    return () => clearInterval(interval)
  }, [buildingHasElements, dispatch, fileName])

  useEffect(() => {
    if (!planner.current) {
      return
    }

    if (!selectedTool) {
      if (selectedConstructionPage === ConstructionPage.Engineering) {
        planner.current.toggleStructuralElementsPicking(true)
        planner.current.toggleBuildingElementsPicking(
          [BuildingElementType.Wall],
          false,
        )
      }

      return
    }

    switch (selectedTool) {
      case BuildingObjectTool.Walls:
        planner.current.enableCreateWallPickDownTrigger()
        break
      case BuildingObjectTool.Windows:
        planner.current.enableCreateWindowPickDownTrigger()
        break
      case BuildingObjectTool.Doors:
        planner.current.enableCreateDoorPickDownTrigger()
        break
      case BuildingStructuralObjectTool.Beams:
        planner.current.toggleStructuralElementsPicking(false)
        planner.current.toggleBuildingElementsPicking(
          [BuildingElementType.Wall],
          true,
        )
        planner.current.enableCreateBeamPickDownTrigger()
        break
      case BuildingEditingTool.DirectSelect:
        if (selectedConstructionPage === ConstructionPage.Engineering) {
          planner.current.enableEditBeamOnPickDownTrigger()
        }
        break
      case BuildingEditingTool.Marquee:
        planner.current.enableMarqueeSelection()
        break
      default:
        break
    }

    return () => {
      switch (selectedTool) {
        case BuildingObjectTool.Walls:
        case BuildingObjectTool.Windows:
        case BuildingObjectTool.Doors:
        case BuildingStructuralObjectTool.Beams:
        case BuildingEditingTool.DirectSelect:
          planner.current?.disableElementPickDownTrigger()
          break
        case BuildingEditingTool.Marquee:
          planner.current?.disableMarqueeSelection()
          break
        default:
          break
      }
    }
  }, [selectedTool, selectedConstructionPage])

  useEffect(() => {
    planner.current?.updateGrid()
  }, [gridSettings])

  useEffect(() => {
    const loadBimObjects = async () => {
      if (buildingBimObjects.length) {
        dispatch(updateStatusMessage('Loading BIM objects...'))
        await planner.current?.createBimObjects(building, buildingBimObjects)
        dispatch(updateStatusMessage('Done'))
      }
    }

    loadBimObjects()
  }, [buildingBimObjects])

  return (
    <Box
      onClick={handleAddCommentClick}
      sx={{
        padding: 0,
        margin: 0,
        display: 'grid',
        gridTemplateColumns: '100%',
        gridTemplateRows: '100%',
        gridColumn: 1,
        gridRow: 1,
        overflow: 'hidden',
        width: '100%',
        height: '100%',
        cursor: isCommentsMode
          ? `url(${commentsCursor}) 0 20, auto`
          : selectedTool === BuildingStructuralObjectTool.Beams
            ? `url(${addCursor}) 0 20, auto`
            : 'default',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          padding: 0,
          margin: 0,
          overflow: 'unset',
          display: 'block',
          borderRadius: '4px',
          border: 'none',
          outline: 'none',
          pointerEvents: isCommentsMode ? 'none' : 'all',
        }}
      />

      <OptionsModal />
    </Box>
  )
})

export default Viewer
